<template>
  <div class="my-4">
    <GridBlogList
      :post-list="data?.posts"
      show-featured
    >
      <template #after-feature>
        <div class="inline-flex items-center gap-4 my-4">
          <span class="font-semibold">{{ t('pages.blog.filters') }}</span>

          <TSelect
            id="topics"
            v-model="selectedTopic"
            :options="mappedTopics"
            class="max-w-32"
          />
        </div>
      </template>
    </GridBlogList>

    <GhostPagination
      :pagination="data?.meta?.pagination"
      :is-disabled="pending"
      class="mt-10 mb-6"
    />
  </div>
</template>

<script setup lang="ts">
const route = useRoute()
const { locale, t, te } = useI18n()
const localePath = useLocalePath()
const { getPosts, getTags } = useGhostApi()

definePageMeta({
  i18n: {
    paths: {
      en: '/blog',
      fr: '/blogue',
    },
  },
})

const page = computed(() => route.query.page ? Number(route.query.page) : 1)

const { data, pending } = await useAsyncData(
  computed(() => `blog-${locale.value}-${page.value}`),
  async () => {
    const [{ posts, meta }, topics] = await Promise.all([
      getPosts({ page: page.value }),
      getTags(),
    ])
    return { posts, meta, topics }
  },
)

const mappedTopics = computed(() => [
  { label: t('pages.blog.topics'), value: '', disabled: true },
  ...(data.value?.topics || []).filter((tag) => {
    return te(`tags.${tag.slug}`, locale.value)
  }).map((tag) => ({
    label: `${tag.name} (${tag.count?.posts ?? 0})`,
    value: tag.slug ?? '',
  }))])

useSeoMeta({
  title: t('pages.blog.metaTitle'),
  description: t('pages.blog.metaDesc'),
  ogTitle: t('pages.blog.metaTitle'),
  ogDescription: t('pages.blog.metaDesc'),
  twitterTitle: t('pages.blog.metaTitle'),
  twitterDescription: t('pages.blog.metaDesc'),
})

/**
 * Redirects to a topic.
 *
 * @param topic The topic to redirect to
 * @returns A promise that resolves when the topic is redirected to
 */
async function redirectToTopic(topic: string) {
  await navigateTo(localePath({
    name: 'category-slug',
    params: {
      slug: topic,
    },
  }))
}

const selectedTopic = ref('')
watch(selectedTopic, async (newTopic) => {
  if (newTopic) {
    await redirectToTopic(newTopic)
  }
})
</script>

<i18n src="~~/i18n/locales/common/blog.json" lang="json" />

<i18n src="~~/i18n/locales/common/pages/blog.json" lang="json" />
